/**
 * Solana launch page — wires the existing LaunchForm UI to a real Meteora DLMM
 * launch via the wallet adapter. No on-chain `wick_factory` yet; this calls
 * the DLMM SDK directly using the same flow proven in `solana/tests/battle-test.ts`.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useConnection, useWallet } from "@solana/wallet-adapter-react";
import { WalletMultiButton } from "@solana/wallet-adapter-react-ui";
import { useNavigate } from "react-router-dom";

import LaunchForm from "./premium/LaunchForm";
import { launchToken } from "../lib/solanaLaunch";
import { slurpApi } from "../lib/slurpApi";
import { useSolPrice } from "../lib/solPrice";

/** Stage codes emitted by solanaLaunch.js → friendly label + step position. */
// Two-tx atomic launch flow:
//   Tx 1: mint + supply + metadata + revoke authority (step 1)
//   Tx 2: pool create + LP seed + optional atomic dev buy (step 2)
const STAGES = {
  "creating-mint":            { label: "Preparing your token",    step: 1 },
  "mint-created":             { label: "Preparing your token",    step: 1 },
  "creating-pool":            { label: "Preparing your token",    step: 2 },
  "dev-buy-bundled":          { label: "Preparing your token",    step: 2 },
  "pool-and-fees":            { label: "Preparing your token",    step: 2 },
  "pool-created":             { label: "Preparing your token",    step: 2 },
  "dev-buy-complete":         { label: "Preparing your token",    step: 2 },
  "fee-split-done":           { label: "Preparing your token",    step: 2 },
  "done":                     { label: "Launched!",               step: 2 },
};
const TOTAL_STEPS = 2;

const MAX_FILE_SIZE = 1024 * 1024;

export default function SolanaDeployTokenPage() {
  const { connection } = useConnection();
  const wallet = useWallet();
  const isConnected = !!wallet.publicKey;
  const { price: solUsd } = useSolPrice();
  const navigate = useNavigate();

  // Warm the Meteora SDK import while the user is filling out the form so
  // the first launch click doesn't eat a 200-500ms lazy-load cost on top
  // of everything else. Fire-and-forget; failures are non-fatal since the
  // real launch call will re-import on demand.
  useEffect(() => {
    import(/* webpackPrefetch: true */ "@meteora-ag/cp-amm-sdk").catch(() => {});
  }, []);

  // ----- form state -----
  const [tokenName, setTokenName] = useState("");
  const [tokenSymbol, setTokenSymbol] = useState("");
  const [description, setDescription] = useState("");
  const [tokenSupply, setTokenSupply] = useState("1000000000");
  const [website, setWebsite] = useState("");
  const [twitter, setTwitter] = useState("");
  const [telegram, setTelegram] = useState("");

  // image upload
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [imageError, setImageError] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // dev buy (SOL)
  const [deploymentFee, setDeploymentFee] = useState("");
  const updateDeploymentFee = useCallback((v) => {
    setDeploymentFee((v || "").replace(/[^0-9.]/g, ""));
  }, []);

  // launch state
  const [isExecuting, setIsExecuting] = useState(false);
  const [stage, setStage] = useState(null);
  const [txHash, setTxHash] = useState(null);
  const [txResult, setTxResult] = useState(null);
  const [error, setError] = useState(null);
  const lastLaunchRef = useRef(null);

  // Auto-navigate to the token page ~1.5s after a successful launch.
  // Give users a moment to see the success flash before routing.
  useEffect(() => {
    if (!txResult?.mint) return;
    const t = setTimeout(() => {
      navigate(`/token/${txResult.mint}`);
    }, 1500);
    return () => clearTimeout(t);
  }, [txResult, navigate]);

  // file upload (Cloudinary, same flow as the EVM page)
  const handleFileChange = useCallback(async (event) => {
    const file = event.target.files?.[0];
    setImageError(null);
    if (!file) return;
    if (file.size > MAX_FILE_SIZE) {
      setImageError("Image too big — keep it under 1 MB");
      return;
    }
    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
    setIsUploading(true);
    setUploadProgress(0);
    try {
      const { uploadUrl, publicUrl } = await slurpApi.signUpload({
        contentType: file.type,
        size: file.size,
        prefix: "tokens",
      });
      const put = await fetch(uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": file.type },
        body: file,
      });
      if (!put.ok) throw new Error(`Upload failed (${put.status})`);
      setImageUrl(publicUrl);
      setUploadProgress(100);
    } catch (err) {
      setImageError(err.message || "Upload failed");
    } finally {
      setIsUploading(false);
    }
  }, []);

  // fee tier chosen in the form goes here; LaunchForm tracks it locally and we
  // don't currently feed it into the DLMM call (1% is hardcoded for memecoins).
  const deployToken = useCallback(
    async (opts = {}) => {
      setError(null);
      if (!isConnected) {
        setError("Connect your Solana wallet first");
        return;
      }
      if (!solUsd) {
        setError("Live SOL price not loaded yet — wait a sec and try again");
        return;
      }
      const supply = Number(tokenSupply);
      if (!Number.isFinite(supply) || supply <= 0) {
        setError("Pick a token supply");
        return;
      }
      const startingMcapUsd = Number(opts.startingMcapUsd) > 0 ? Number(opts.startingMcapUsd) : 5000;
      const devBuySol = Number(opts.devBuyAmount) > 0 ? Number(opts.devBuyAmount) : 0;
      const feeMode =
        opts.feeMode === "burn" ? "burn" :
        opts.feeMode === "lp_compound" ? "lp_compound" :
        "claim";
      const notifyFollowers = !!opts.notifyFollowers;

      // Upfront wallet balance check — DAMM v2 launch cost ≈ 0.05 SOL
      // (cheaper than DLMM's 0.19). Bail before spending anything if the
      // wallet can't cover the whole flow.
      try {
        const lamports = await connection.getBalance(wallet.publicKey, "confirmed");
        const sol = lamports / 1e9;
        const needed = 0.05 + devBuySol;
        if (sol < needed) {
          setError(
            `Need ~${needed.toFixed(3)} SOL but wallet only has ${sol.toFixed(4)} SOL. Top up and retry.`
          );
          return;
        }
      } catch {
        /* non-fatal — continue and let the real tx error surface */
      }

      setIsExecuting(true);
      setStage(null);
      setTxHash(null);
      setTxResult(null);
      try {
        const result = await launchToken({
          connection,
          wallet,
          name: tokenName,
          symbol: tokenSymbol,
          supplyWhole: supply,
          startingMcapUsd,
          solUsd,
          devBuySol,
          imageUrl,
          description,
          website,
          twitter,
          telegram,
          feeMode,
          notifyFollowers,
          onStage: (s, detail) => {
            setStage(s);
            if (detail?.sig) setTxHash(detail.sig);
          },
        });
        setTxResult(result);
        lastLaunchRef.current = result;
      } catch (err) {
        console.error("launch failed", err);
        setError(err?.message || "Launch failed");
      } finally {
        setIsExecuting(false);
      }
    },
    [connection, wallet, isConnected, solUsd, tokenName, tokenSymbol, tokenSupply, imageUrl, description, website, twitter, telegram]
  );

  const formProps = useMemo(
    () => ({
      isExecuting,
      signer: wallet.publicKey ? wallet : null,
      tokenName,
      setTokenName,
      tokenSymbol,
      setTokenSymbol,
      description,
      setDescription,
      selectedFile,
      previewUrl,
      imageUrl,
      imageError,
      isUploading,
      uploadProgress,
      handleFileChange,
      tokenSupply,
      setTokenSupply,
      deploymentFee,
      updateDeploymentFee,
      website,
      setWebsite,
      twitter,
      setTwitter,
      telegram,
      setTelegram,
      selectedNetwork: "SOLANA",
      getMaxDeploymentFee: () => 10, // 10 SOL ceiling on dev buy
      solUsd,
      deployToken,
      txHash,
      txResult,
    }),
    [
      isExecuting,
      wallet,
      tokenName,
      tokenSymbol,
      description,
      selectedFile,
      previewUrl,
      imageUrl,
      imageError,
      isUploading,
      uploadProgress,
      handleFileChange,
      tokenSupply,
      deploymentFee,
      updateDeploymentFee,
      website,
      twitter,
      telegram,
      solUsd,
      deployToken,
      txHash,
      txResult,
    ]
  );

  if (!isConnected) {
    return (
      <div className="pm-launch pm-launch--flow">
        <div className="pm-q" style={{ alignItems: "center", textAlign: "center" }}>
          <div className="pm-q__ask">Connect your wallet to launch.</div>
          <div className="pm-q__hint">
            Phantom, Solflare, or Ledger — all good. Your wallet pays the rent and signs
            the transactions.
          </div>
          <div style={{ marginTop: 24 }}>
            <WalletMultiButton />
          </div>
          <button
            type="button"
            onClick={() => {
              // Escape hatch for users stuck on a wallet they can't unlock
              // (e.g. forgotten Solflare password). Clears the persisted
              // walletName so autoConnect on next load won't silently re-grab
              // the same broken wallet, plus disconnects the current session.
              try { window.localStorage.removeItem("walletName"); } catch { /* noop */ }
              try { window.location.reload(); } catch { /* noop */ }
            }}
            style={{
              marginTop: 14,
              background: "none",
              border: "none",
              padding: 0,
              color: "#c9bca0",
              fontSize: 12,
              textDecoration: "underline",
              cursor: "pointer",
            }}
          >
            Trouble connecting? Reset wallet state
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <LaunchForm {...formProps} />
      <div
        style={{
          position: "fixed",
          bottom: 14,
          left: 14,
          background: solUsd ? "#fae039" : "#1a1714",
          color: solUsd ? "#0a0807" : "#c9bca0",
          border: "2.5px solid #f4ecd8",
          padding: "6px 12px",
          borderRadius: 4,
          zIndex: 999,
          fontFamily: "JetBrains Mono, monospace",
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          transform: "rotate(-2deg)",
          boxShadow: "3px 3px 0 0 #f4ecd8",
        }}
        title="Live SOL/USD — used to pin starting market cap"
      >
        SOL · {solUsd ? `$${solUsd.toFixed(2)}` : "loading…"}
      </div>
      {error && (
        <div className="pm-launch-toast pm-launch-toast--error">
          <span className="pm-launch-toast__dot" aria-hidden="true" />
          <span className="pm-launch-toast__text">{error}</span>
        </div>
      )}
      {stage && isExecuting && !error && (() => {
        const info = STAGES[stage] || { label: stage.replace(/-/g, " "), step: 1 };
        // Pre-sign / mint stage stays as a small toast at the bottom
        // because the user is still actively interacting (signing). Once
        // we hit step 2 (pool + fees + receipt verification — anywhere
        // up to ~30s of waiting), promote to a full-screen modal so the
        // user understands the page is busy and they shouldn't refresh.
        if (info.step < 2) {
          return (
            <div className="pm-launch-toast pm-launch-toast--progress">
              <span className="pm-launch-toast__spinner" aria-hidden="true" />
              <span className="pm-launch-toast__text">
                {info.label}
                <span className="pm-launch-toast__count"> · step {info.step} of {TOTAL_STEPS}</span>
              </span>
              <span className="pm-launch-toast__bar" aria-hidden="true">
                <span style={{ width: `${(info.step / TOTAL_STEPS) * 100}%` }} />
              </span>
            </div>
          );
        }
        return (
          <div className="pm-launch-blocker" role="dialog" aria-modal="true" aria-label="Launching">
            <div className="pm-launch-blocker__card">
              <div className="pm-launch-blocker__spinner" aria-hidden="true" />
              <div className="pm-launch-blocker__title">{info.label}</div>
              <div className="pm-launch-blocker__sub">
                {stage === "creating-pool" || stage === "dev-buy-bundled" || stage === "pool-and-fees"
                  ? "Sign the second transaction in your wallet. This opens the pool, seeds initial liquidity, and locks LP."
                  : "Finalising your launch and getting your token page ready..."}
              </div>
              <div className="pm-launch-blocker__warn">
                Do <b>NOT</b> refresh or close this tab. This usually takes 10 to 30 seconds.
              </div>
            </div>
          </div>
        );
      })()}
      {txResult && (
        <button
          type="button"
          className="pm-launch-toast pm-launch-toast--success"
          onClick={() => navigate(`/token/${txResult.mint}`)}
          title="Go to token page"
        >
          <span className="pm-launch-toast__check" aria-hidden="true">✓</span>
          <span className="pm-launch-toast__text">
            Launched! Routing to token page
            <span className="pm-launch-toast__sub">
              {txResult.mint.slice(0, 6)}…{txResult.mint.slice(-4)}
            </span>
          </span>
        </button>
      )}
    </>
  );
}
